import React, { useRef, useState } from 'react';
import { IconUpload } from '@tabler/icons-react';
import { cn } from '@/utils/cn';
import { TeeButton } from '@/components/ui/tee-button';

export interface TeeDropZoneProps {
  label?: string;
  hint?: string;
  /** `<input accept>` value, e.g. "image/png,image/jpeg,image/webp" or "video/*" */
  accept: string;
  maxSizeMb: number;
  /** Controlled: the currently selected local file, if any */
  file: File | null;
  /** Existing remote media preview (edit mode) shown when `file` is null */
  previewUrl?: string | null;
  onFileSelected: (file: File) => void;
  onFileCleared: () => void;
  onError: (message: string) => void;
  disabled?: boolean;
  idleText: string;
  browseText: string;
  removeText: string;
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ZONE_BASE_CLASSES =
  'flex min-h-[160px] w-full flex-col items-center justify-center gap-2 rounded-[8px] border-2 border-dashed p-4';

function isAcceptedType(fileType: string, accept: string): boolean {
  const entries = accept
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (entries.length === 0) return true;
  return entries.some((entry) => {
    if (entry.endsWith('/*')) {
      const prefix = entry.slice(0, entry.indexOf('/*'));
      return fileType.startsWith(`${prefix}/`);
    }
    return fileType === entry;
  });
}

export const TeeDropZone: React.FC<TeeDropZoneProps> = ({
  label,
  hint,
  accept,
  maxSizeMb,
  file,
  previewUrl,
  onFileSelected,
  onFileCleared,
  onError,
  disabled = false,
  idleText,
  browseText,
  removeText,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const validateAndSelect = (candidate: File | undefined | null) => {
    if (!candidate) return;
    if (candidate.size > maxSizeMb * 1024 * 1024) {
      onError(`File exceeds the ${maxSizeMb}MB size limit.`);
      return;
    }
    if (!isAcceptedType(candidate.type, accept)) {
      onError(`File type "${candidate.type || 'unknown'}" is not supported.`);
      return;
    }
    onFileSelected(candidate);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const candidate = e.target.files?.[0] ?? null;
    validateAndSelect(candidate);
    e.target.value = '';
  };

  const openFileBrowser = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragOver(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const candidate = e.dataTransfer.files?.[0] ?? null;
    validateAndSelect(candidate);
  };

  const isImageAccept = accept.startsWith('image/');

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label && (
        <label className="text-[14px] font-normal text-[var(--text-content-title)]">
          {label}
        </label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleInputChange}
        disabled={disabled}
      />

      {file ? (
        <div className={cn(ZONE_BASE_CLASSES, 'border-[var(--border-default)]')}>
          <span className="max-w-full truncate text-[14px] font-normal text-[var(--text-content-title)]">
            {file.name}
          </span>
          <span className="text-[14px] font-normal text-[var(--text-content-sub-title)]">
            {formatFileSize(file.size)}
          </span>
          <TeeButton
            variant="ghost"
            size="s"
            text={removeText}
            onClick={onFileCleared}
            disabled={disabled}
          />
        </div>
      ) : previewUrl ? (
        <div className={cn(ZONE_BASE_CLASSES, 'border-[var(--border-default)]')}>
          {isImageAccept ? (
            <img
              src={previewUrl}
              alt={label ?? idleText}
              className="max-h-[96px] rounded-[6px] object-contain"
            />
          ) : (
            <span className="max-w-full truncate text-[14px] font-normal text-[var(--text-content-title)]">
              {previewUrl}
            </span>
          )}
          <TeeButton
            variant="ghost"
            size="s"
            text={removeText}
            onClick={onFileCleared}
            disabled={disabled}
          />
        </div>
      ) : (
        <button
          type="button"
          aria-label={label ?? idleText}
          disabled={disabled}
          onClick={openFileBrowser}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            ZONE_BASE_CLASSES,
            'cursor-pointer transition-colors',
            isDragOver
              ? 'border-[var(--border-active)] bg-[var(--background-card-hover)]'
              : 'border-[var(--border-default)]',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <IconUpload
            size={24}
            className="text-[var(--text-content-sub-title)]"
          />
          <span className="text-[14px] font-normal text-[var(--text-content-title)]">
            {idleText}
          </span>
          <TeeButton
            variant="secondary"
            size="s"
            text={browseText}
            onClick={(e) => {
              e.stopPropagation();
              openFileBrowser();
            }}
            disabled={disabled}
          />
        </button>
      )}

      {hint && (
        <p className="text-[14px] font-normal text-[var(--text-content-sub-title)]">
          {hint}
        </p>
      )}
    </div>
  );
};

TeeDropZone.displayName = 'TeeDropZone';

export default TeeDropZone;
